import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import { imgUrlChk } from '@/utils/function';
import useContentHtmlPostprocess from './useContentHtmlPostprocess';
import {
  buildReturnRequestHtml,
  buildWithdrawalRejectionHtml,
} from './statusMapping';
import {
  LogRoot,
  LogHeader,
  LogHeaderLeft,
  LogProfileImage,
  LogNameText,
  LogTitleText,
  LogChip,
  LogCreatedAt,
  LogContent,
} from './styles';
import type { ApprovalLogItem, ApprovalLogTone } from './CommentSlot.types';

/**
 * 결재 액션 로그 행 — 포탈 공용 CommentItem 의 자매 컴포넌트. 좋아요/수정/삭제/번역 없음.
 *
 * return_request(반려요청)는 결재 액션이 아니라서 목록 단계(useApprovalComments)에서 걸러진다.
 * 여기 매핑을 남겨 둔 이유는, 걸러지지 않은 행이 흘러들어도 '상신'으로 잘못 표시되지 않게 하기 위함이다.
 */

const ACTION_TONE: Record<ApprovalLogItem['action'], ApprovalLogTone> = {
  create: 'default',
  temporary: 'default',
  confirm: 'confirm',
  reject: 'reject',
  return: 'reject',
  return_request: 'reject',
  withdraw: 'reject',
  cancel: 'reject',
  hold: 'hold',
};

type Props = {
  item: ApprovalLogItem;
};

const ApprovalLogRow = ({ item }: Props) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement | null>(null);

  // v2 회수·반려요청만 raw 사유를 고정 안내 문구로 감싼다 — v1 return/reject 는 이미 풀포맷.
  const contentHtml = useMemo(() => {
    const raw = item.content && item.content.trim() ? item.content : null;
    if (item.action === 'withdraw') {
      return buildWithdrawalRejectionHtml(raw, t);
    }
    if (item.action === 'return_request') {
      return buildReturnRequestHtml(raw, t);
    }
    return raw;
  }, [item.action, item.content, t]);
  useContentHtmlPostprocess(contentRef, contentHtml);

  const actionLabel = useMemo(() => {
    switch (item.action) {
      case 'confirm':
        return t('승인', { context: 'status' });
      case 'reject':
      case 'return':
      case 'withdraw':
        return t('반려', { context: 'status' });
      case 'return_request':
        return t('반려요청', { context: 'status' });
      case 'hold':
        return t('보류', { context: 'status' });
      case 'temporary':
        return t('임시저장', { context: 'status' });
      case 'cancel':
        return t('취소', { context: 'status' });
      case 'create':
        return t('상신');
      default:
        // 서버에 새 로그 종류가 생겨도 '상신' 등으로 잘못 보이지 않게 이름을 비움
        return null;
    }
  }, [item.action, t]);

  const tone = ACTION_TONE[item.action] ?? 'default';
  const { name, positionName, profileImageUrl } = item.createdBy;

  return (
    <LogRoot tone={tone}>
      <LogHeader>
        <LogHeaderLeft>
          <LogProfileImage>
            <ImageWithErrorHandling
              src={imgUrlChk(profileImageUrl)}
              width={32}
              height={32}
              alt={t('프로필 이미지')}
            />
          </LogProfileImage>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              // 좁은 폭에서 이름이 말줄임 되도록 — 없으면 min-content 로 버티며 행이 넘침
              minWidth: 0,
            }}
          >
            <LogNameText>
              {name} {positionName}
            </LogNameText>
            <LogTitleText>{t('님의 결재 업데이트')}</LogTitleText>
            {actionLabel && <LogChip tone={tone}>{actionLabel}</LogChip>}
          </Box>
        </LogHeaderLeft>
        <LogCreatedAt>{item.createdAt}</LogCreatedAt>
      </LogHeader>
      {contentHtml && (
        <LogContent
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />
      )}
    </LogRoot>
  );
};

export default ApprovalLogRow;
